import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { getChainConfig } from "@thema/sdk/ui";
import { useVaultAssetMetadata, useVaultConfig, useWalletCapStatus } from "@thema/ui";
import { CONSENT_DEPLOYMENT_ID, CONSENT_VAULT_REF } from "./consent-config";
const EMAIL_STORAGE_KEY = "thema:early-access:email";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isAddress(value: string | null | undefined): value is `0x${string}` {
  return !!value && /^0x[a-fA-F0-9]{40}$/.test(value);
}

export default function Done() {
  const [params] = useSearchParams();
  const txHash = params.get("tx");
  const wallet = params.get("wallet");
  const vault = useVaultConfig(CONSENT_DEPLOYMENT_ID, CONSENT_VAULT_REF);

  const [copied, setCopied] = useState<"wallet" | "tx" | null>(null);

  const email = (() => {
    try {
      const raw = sessionStorage.getItem(EMAIL_STORAGE_KEY) ?? "";
      return EMAIL_REGEX.test(raw.trim()) ? raw.trim() : "";
    } catch {
      return "";
    }
  })();

  // Whitelist gate for the deposit CTA. We probe regardless of where the
  // wallet came from (URL param or none) so the CTA can render once the user
  // is approved without forcing them to revisit the consent step.
  const walletAddr = isAddress(wallet) ? (wallet.toLowerCase() as `0x${string}`) : undefined;
  const { metadata: assetMeta } = useVaultAssetMetadata({ vault });
  const capDecimals = assetMeta
    ? {
        assetDecimals: assetMeta.asset.decimals,
        accountingDecimals: assetMeta.accountingBase.decimals,
      }
    : undefined;
  const { status: capStatus, isLoading: capLoading } = useWalletCapStatus({
    vault,
    wallet: walletAddr,
    decimals: capDecimals,
  });
  const isWhitelisted = capStatus?.configured === true;

  // Hooks must run before any conditional return.
  const explorerUrl = useMemo(() => {
    if (!txHash || !vault) return undefined;
    try {
      const { chain } = getChainConfig(vault.chainId);
      const base = chain.blockExplorers?.default?.url;
      return base ? `${base.replace(/\/$/, "")}/tx/${txHash}` : undefined;
    } catch {
      return undefined;
    }
  }, [txHash, vault]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(null), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  // Direct navigation with no context — bounce to step 1.
  // We still allow rendering when only `wallet` is present (a non-whitelisted
  // returning user routed here from the Consent "Next Step" button).
  if (!txHash && !wallet) return <Navigate to="/early-access" replace />;

  const copy = async (text: string, kind: "wallet" | "tx") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <main className="min-h-screen bg-[#0A0F16] text-thema-text px-6 py-12 md:py-20">
      <article className="mx-auto max-w-[560px] space-y-8">
        <header className="space-y-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-thema-teal/80">
            CONSENT RECORDED
          </p>
          <h1 className="font-serif text-2xl md:text-3xl font-semibold leading-tight">
            Consent Recorded On-Chain.
          </h1>
          <p className="text-[14px] leading-relaxed text-thema-text-soft/80">
            Thank you,
          </p>
          <p className="text-[14px] leading-relaxed text-thema-text-soft/80 text-justify">
            Your consent has been noted and is now part of the public log on{" "}
            {vault?.chainName ?? "Base"}. Once your whitelist request is
            accepted, you&rsquo;ll receive an email from{" "}
            <a
              href="mailto:early-access@thema.financial"
              className="text-thema-royal-blue hover:underline"
            >
              early-access@thema.financial
            </a>{" "}
            with the next steps.
          </p>
        </header>

        <section className="rounded-md border border-thema-border-hairline bg-white/[0.015] p-5 space-y-4 text-[13px]">
          {email && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
                Email
              </p>
              <p className="text-thema-text-soft break-all mt-1">{email}</p>
              <p className="mt-1 text-[11px] text-thema-text-soft/60">
                Bound to this wallet&rsquo;s consent. All vault communications go here.
              </p>
            </div>
          )}

          {wallet && (
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
                  Wallet
                </p>
                <p className="font-mono text-thema-text-soft break-all mt-1">{wallet}</p>
              </div>
              <button
                type="button"
                onClick={() => copy(wallet, "wallet")}
                className="font-mono text-[10px] uppercase tracking-[0.15em] text-thema-royal-blue hover:underline"
              >
                {copied === "wallet" ? "Copied" : "Copy"}
              </button>
            </div>
          )}

          {txHash && (
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
                  Transaction
                </p>
                <p className="font-mono text-thema-text-soft break-all mt-1">{txHash}</p>
              </div>
              <button
                type="button"
                onClick={() => copy(txHash, "tx")}
                className="font-mono text-[10px] uppercase tracking-[0.15em] text-thema-royal-blue hover:underline"
              >
                {copied === "tx" ? "Copied" : "Copy"}
              </button>
            </div>
          )}

          {txHash && explorerUrl && (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-block font-mono text-[11px] uppercase tracking-[0.15em] text-thema-royal-blue hover:underline"
            >
              View on block explorer →
            </a>
          )}
        </section>

        {walletAddr && (
          <section className="rounded-md border border-thema-border-hairline bg-white/[0.015] p-5 space-y-3">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-micro">
              Next step
            </h2>
            {capLoading && (
              <p className="text-[12px] text-thema-micro">
                Checking deposit access…
              </p>
            )}
            {!capLoading && isWhitelisted && (
              <>
                <p className="text-[13px] text-thema-text-soft/85 leading-relaxed">
                  Your wallet is approved for the {vault?.omtName ?? ""} vault.
                  Continue to deposit.
                </p>
                <Link
                  to={`/welcome-${walletAddr}`}
                  className="inline-flex items-center justify-center w-full px-5 py-3 rounded-md border border-thema-royal-blue/60 bg-thema-royal-blue/15 text-white text-sm font-medium transition hover:bg-thema-royal-blue/25"
                >
                  Continue to deposit →
                </Link>
              </>
            )}
            {!capLoading && !isWhitelisted && (
              <p className="text-[12px] text-thema-text-soft/70 leading-relaxed">
                You'll see a "Continue to deposit" button here once your wallet is
                added to the deposit list. Refresh this page after the email
                confirmation lands.
              </p>
            )}
          </section>
        )}

        <div className="pt-2">
          <Link
            to="/"
            className="font-mono text-[11px] uppercase tracking-[0.15em] text-thema-micro hover:text-white/70"
          >
            ← Back to thema.financial
          </Link>
        </div>
      </article>
    </main>
  );
}
